import { Badge, Box, Center, Flex, Link, Progress, Spinner, Stack, Text } from "@chakra-ui/react";
import type { FunctionComponent } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";
import { AwardMethodDto, useGetEvaluationQuery } from "../../../../autogen/bff-api";
import { urls } from "../../../../urls";
import { deriveBidEvaluationSummary, formatRankLabel, type BidEvaluationCriterion, type BidEvaluationSummary } from "./bidEvaluationSummary";

// t("Lowest price") t("Best price-quality ratio")
// t("points") t("weighted") t("of {{max}}") t("#{{rank}} of {{totalBids}} bids") t("Weighted score") t("Price score")

const MAX_SCORE = 10;

interface Props {
  eventId: string;
  bidId: string;
}

export const EvaluationTab: FunctionComponent<Props> = ({ eventId, bidId }) => {
  const { data, isLoading, isError } = useGetEvaluationQuery({ eventId });
  const summary = deriveBidEvaluationSummary(data, bidId, { isLoading, isError });
  return <EvaluationTabBody summary={summary} eventId={eventId} />;
};

export const EvaluationTabBody = ({ summary, eventId }: { summary: BidEvaluationSummary; eventId: string }) => {
  const { t } = useTranslation();
  if (summary.state === "loading") {
    return (
      <Center py="16">
        <Spinner color="smPrimary" />
      </Center>
    );
  }
  if (summary.state === "error") return <EmptyState text={t("Evaluation could not be loaded.")} />;
  if (summary.state === "no-criteria") {
    return <EmptyState text={t("Evaluation criteria have not been set up yet.")} />;
  }
  if (summary.state === "missing-from-eval") {
    return <EmptyState text={t("The bid is not included in the evaluation.")} />;
  }
  if (summary.state === "not-evaluated") {
    return (
      <Stack spacing="6">
        <NotEvaluatedHeader />
        <CrosslinkNote eventId={eventId} />
      </Stack>
    );
  }
  return <EvaluatedView summary={summary} eventId={eventId} />;
};

const EmptyState = ({ text }: { text: string }) => (
  <Flex
    direction="column"
    align="center"
    justify="center"
    gap="2"
    py="16"
    rounded="xl"
    bg="smBackground"
    border="1px dashed"
    borderColor="smBorder"
    color="smMuted"
  >
    <Text fontWeight="medium">{text}</Text>
  </Flex>
);

const NotEvaluatedHeader = () => {
  const { t } = useTranslation();
  return (
    <Box bg="smBackgroundSecondary" border="1px solid" borderColor="smBorder" rounded="xl" px="5" py="4">
      <Text fontSize="xs" fontWeight="bold" letterSpacing="wider" textTransform="uppercase" color="smMuted">
        {t("Ranking")}
      </Text>
      <Text fontSize="lg" fontWeight="bold" color="smText">
        {t("Not evaluated yet")}
      </Text>
    </Box>
  );
};

const CrosslinkNote = ({ eventId }: { eventId: string }) => {
  const procurementEvalUrl = `${urls.v2.sourcingEvents.view.go(eventId)}?tab=evaluation`;
  return (
    <Text fontSize="sm" color="smMuted">
      {/*
        Custom-component slot is named `evalTab` (not `link`) — react-i18next
        treats `<link>` in the i18nKey as the HTML void element and renders it
        empty, leaving the inner text outside the anchor.
      */}
      <Trans
        i18nKey="Scoring is registered in the procurement's <evalTab>Evaluation tab</evalTab>."
        components={{
          evalTab: <Link as={RouterLink} to={procurementEvalUrl} color="smPrimary" />,
        }}
      />
    </Text>
  );
};

const EvaluatedView = ({
  summary,
  eventId,
}: {
  summary: Extract<BidEvaluationSummary, { state: "evaluated" }>;
  eventId: string;
}) => (
  <Stack spacing="6">
    <EvaluatedHeader summary={summary} />
    <Stack spacing="3">
      {summary.criteria.map((criterion) => (
        <CriterionCard key={criterion.id} criterion={criterion} />
      ))}
    </Stack>
    <CrosslinkNote eventId={eventId} />
  </Stack>
);

const EvaluatedHeader = ({
  summary,
}: {
  summary: Extract<BidEvaluationSummary, { state: "evaluated" }>;
}) => {
  const { t } = useTranslation();
  const isWinner = summary.rank === 1;
  const methodLabel = summary.awardMethod === AwardMethodDto.LowestPrice ? t("Lowest price") : t("Best price-quality ratio");
  return (
    <Flex
      data-testid="rank-header"
      data-winner={isWinner ? "true" : "false"}
      align="center"
      gap="4"
      px="5"
      py="3"
      rounded="xl"
      border="1px solid"
      borderColor={isWinner ? "green.200" : "smBorder"}
      bg={isWinner ? "green.50" : "smBackgroundSecondary"}
    >
      <Flex
        data-testid="rank-circle"
        align="center"
        justify="center"
        boxSize="10"
        rounded="full"
        bg={isWinner ? "smPrimary" : "smBackground"}
        color={isWinner ? "white" : "smMuted"}
        border={isWinner ? undefined : "1px solid"}
        borderColor="smBorder"
        fontWeight="bold"
        flexShrink={0}
      >
        {summary.rank}
      </Flex>
      <Box flex="1">
        <Text fontSize="xs" fontWeight="bold" letterSpacing="wider" textTransform="uppercase" color="smMuted">
          {t("Ranking")}
        </Text>
        <Text fontSize="lg" fontWeight="bold" color="smText">
          {formatRankLabel(summary, t)}
        </Text>
        <Text fontSize="sm" color="smMuted">
          {methodLabel}
        </Text>
      </Box>
      <Box textAlign="right">
        <Text fontSize="xs" fontWeight="bold" letterSpacing="wider" textTransform="uppercase" color="smMuted">
          {summary.awardMethod === AwardMethodDto.LowestPrice ? t("Price score") : t("Weighted score")}
        </Text>
        <Text fontSize="2xl" fontWeight="bold" color={isWinner ? "smPrimary" : "smText"} fontFamily="mono">
          {Number.isFinite(summary.weightedTotal) ? summary.weightedTotal.toFixed(1) : "–"}
        </Text>
        <Text fontSize="xs" color="smMuted" fontFamily="mono">
          {t("of {{max}}", { max: MAX_SCORE.toFixed(1) })}
        </Text>
      </Box>
    </Flex>
  );
};

const CriterionCard = ({ criterion }: { criterion: BidEvaluationCriterion }) => {
  const { t } = useTranslation();
  const rawPoints = criterion.points;
  const rawWeighted = criterion.weightedPoints;
  const hasScore = rawPoints != null && Number.isFinite(rawPoints);
  const isUnscoredPrice = !hasScore && criterion.isPrice;
  // Narrowed inside the hasScore branch — safe to use as number
  const scoredPoints = hasScore ? rawPoints : 0;
  const scoredWeighted = rawWeighted ?? 0;
  return (
    <Box bg="smBackground" border="1px solid" borderColor="smBorder" rounded="xl" px="5" py="4">
      <Flex align="center" gap="3">
        <Text fontWeight="semibold" color="smText">
          {criterion.name ?? t(criterion.type)}
        </Text>
        {criterion.weightPercent != null && (
          <Badge colorScheme="teal">{`${criterion.weightPercent}%`}</Badge>
        )}
      </Flex>
      {hasScore ? (
        <Text fontSize="sm" color="smMuted" mt="1" fontFamily="mono">
          {t("points")} <Text as="span" color="smText" fontWeight="semibold">{scoredPoints.toFixed(1)}</Text>
          {"  ·  "}
          {t("weighted")} <Text as="span" color="smText" fontWeight="semibold">{Number.isFinite(scoredWeighted) ? scoredWeighted.toFixed(1) : "–"}</Text>
        </Text>
      ) : (
        <Text fontSize="sm" color="smMuted" mt="1" fontStyle="italic">
          {isUnscoredPrice ? t("Awaiting price calculation") : t("Not assessed yet")}
        </Text>
      )}
      <Progress
        value={hasScore ? (scoredPoints / MAX_SCORE) * 100 : 0}
        size="sm"
        rounded="full"
        colorScheme="teal"
        bg="smBackgroundSecondary"
        mt="3"
      />
    </Box>
  );
};
